import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  Factory,
  Loader2,
  Monitor,
  TrendingDown,
  CheckCircle2,
  Clock,
  ChevronRight,
  ChevronDown,
  MapPin,
} from 'lucide-react';

function fmtDataBr(ymd) {
  const s = String(ymd || '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function rotuloStatusCarrinho(status) {
  const s = String(status || '');
  if (s === 'em_processamento') return 'em processo';
  if (s === 'disponivel') return 'na fila';
  if (s === 'processado' || s === 'concluido' || s === 'enviado') return 'concluído';
  return s || '—';
}

function resumoSetoresOrdem(porSetor) {
  if (!porSetor?.length) return '';
  return porSetor
    .map((s) => {
      const n = (s.disponiveis || 0) + (s.em_processamento || 0);
      const partes = [];
      if (s.em_processamento) partes.push(`${s.em_processamento} em processo`);
      if (s.disponiveis) partes.push(`${s.disponiveis} na fila`);
      return `${s.setor_nome} (${partes.join(', ') || n})`;
    })
    .join(' · ');
}

const FluxoSetorialPage = () => {
  const navigate = useNavigate();
  const { getEmpresaId } = useAuth();
  const empresaId = getEmpresaId();

  const [setores, setSetores] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [ocupacaoSetores, setOcupacaoSetores] = useState([]);
  const [indicadores, setIndicadores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [expandida, setExpandida] = useState(null);

  const load = useCallback(async () => {
    if (!empresaId || empresaId === 'default') {
      setErro('Empresa não identificada.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro('');
    try {
      const [rSet, rOrd, rInd] = await Promise.all([
        httpsCallable(functions, 'listSetores')({ empresa_id: empresaId, apenas_ativos: true }),
        httpsCallable(functions, 'listOrdensFluxoSetorial')({ empresa_id: empresaId }),
        httpsCallable(functions, 'getIndicadoresProducaoSetorial')({ empresa_id: empresaId }),
      ]);
      setSetores(rSet.data?.data?.items || []);
      setOrdens(rOrd.data?.data?.items || []);
      setOcupacaoSetores(rOrd.data?.data?.ocupacao_setores || []);
      setIndicadores(rInd.data?.data || null);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar.');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    load();
  }, [load]);

  const ocupacaoPorId = useMemo(() => {
    const m = new Map();
    for (const o of ocupacaoSetores) m.set(o.setor_id, o);
    return m;
  }, [ocupacaoSetores]);

  const hojeYmd = useMemo(() => new Date().toLocaleDateString('en-CA'), []);

  const ordensOrdenadas = useMemo(() => {
    return [...ordens].sort((a, b) => {
      const pa = a.fluxo_setorial_iniciado ? 0 : 1;
      const pb = b.fluxo_setorial_iniciado ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return String(a.data_planejada || '').localeCompare(String(b.data_planejada || ''));
    });
  }, [ordens]);

  const qtdAnteriores = useMemo(
    () =>
      ordens.filter((o) => {
        const d = String(o.data_planejada || '');
        return d && d < hojeYmd;
      }).length,
    [ordens, hojeYmd]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <Factory className="h-6 w-6 text-emerald-600" /> Fluxo de Produção por Setor
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Painel da fábrica: onde cada ordem e carrinho está agora. Abra o kiosk do setor no tablet.
        </p>
      </div>

      {erro && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">{erro}</div>
      )}

      {indicadores && (indicadores.entrada_total > 0 || (indicadores.por_setor || []).length > 0) && (
        <section className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border bg-white p-4">
            <p className="text-sm text-gray-500">Rendimento total</p>
            <p className="text-2xl font-bold text-gray-900">
              {indicadores.rendimento_total_pct != null ? `${indicadores.rendimento_total_pct}%` : '—'}
            </p>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <p className="flex items-center gap-1 text-sm text-gray-500">
              <TrendingDown className="h-4 w-4 text-red-500" /> Perda total
            </p>
            <p className="text-2xl font-bold text-gray-900">{indicadores.perda_total ?? 0}</p>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <p className="text-sm text-gray-500">Entrada / Saída</p>
            <p className="text-2xl font-bold text-gray-900">
              {indicadores.entrada_total ?? 0} / {indicadores.saida_total ?? 0}
            </p>
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Painéis de setor</h2>
        {setores.length === 0 ? (
          <p className="rounded-lg bg-white px-4 py-3 text-sm text-gray-400">
            Nenhum setor cadastrado. Crie setores em Parâmetros → Setores.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {setores.map((s) => {
              const oc = ocupacaoPorId.get(s.id);
              const agora = (oc?.disponiveis || 0) + (oc?.em_processamento || 0);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => navigate(`/app/kiosk/${s.id}`)}
                  className="flex items-center justify-between rounded-xl border bg-white p-4 text-left hover:border-emerald-400 hover:shadow-sm"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-emerald-600" />
                      <span className="font-semibold text-gray-900">{s.nome}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {s.total_equipamentos || 0} equipamento(s) · {s.total_usuarios || 0} operador(es)
                    </p>
                    {agora > 0 ? (
                      <p className="mt-2 text-sm font-medium text-emerald-800">
                        Agora: {oc.em_processamento || 0} em processo · {oc.disponiveis || 0} na fila
                      </p>
                    ) : (
                      <p className="mt-2 text-xs text-gray-400">Nenhum carrinho neste setor agora</p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Ordens em andamento na fábrica</h2>
        <p className="mb-3 text-sm text-gray-500">
          Ordens do dia e de dias anteriores ainda abertas
          {qtdAnteriores > 0 ? ` (${qtdAnteriores} de dia anterior)` : ''}.
        </p>
        {ordensOrdenadas.length === 0 ? (
          <p className="rounded-lg bg-white px-4 py-3 text-sm text-gray-400">Nenhuma ordem aberta.</p>
        ) : (
          <div className="space-y-2">
            {ordensOrdenadas.map((o) => {
              const aberta = expandida === o.id;
              const resumo = resumoSetoresOrdem(o.por_setor);
              const totalBat = o.numero_bateladas || o.carrinhos_liberados || 0;
              return (
                <div key={o.id} className="rounded-xl border bg-white">
                  <button
                    type="button"
                    className="flex w-full flex-wrap items-start gap-3 px-4 py-3 text-left"
                    onClick={() => setExpandida(aberta ? null : o.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-lg font-bold text-gray-900">{o.lote_fabricacao}</span>
                      {fmtDataBr(o.data_planejada) ? (
                        <span className="ml-2 text-sm font-medium text-gray-500">{fmtDataBr(o.data_planejada)}</span>
                      ) : null}
                      <p className="truncate text-sm text-gray-600">{o.produto_nome}</p>
                      {String(o.data_planejada || '') && String(o.data_planejada) < hojeYmd ? (
                        <span className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                          Em aberto (dia anterior)
                        </span>
                      ) : null}
                      <p className="mt-0.5 text-xs text-gray-500">
                        {fmtDataBr(o.data_planejada) ? `Planejada em ${fmtDataBr(o.data_planejada)}` : null}
                        {totalBat
                          ? ` · ${o.carrinhos_liberados || 0}/${totalBat} batelada(s) liberada(s)`
                          : null}
                        {o.carrinhos_ativos
                          ? ` · ${o.carrinhos_ativos} carrinho(s) no fluxo`
                          : null}
                      </p>
                      {o.fluxo_setorial_iniciado && resumo ? (
                        <p className="mt-2 flex items-start gap-1.5 text-sm font-medium text-emerald-800">
                          <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                          Agora: {resumo}
                        </p>
                      ) : o.fluxo_setorial_iniciado && !o.carrinhos_ativos ? (
                        <p className="mt-2 text-sm text-gray-500">
                          Carrinhos já saíram das etapas ativas (sem posição atual no chão).
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {o.fluxo_setorial_iniciado ? (
                        <span className="flex items-center gap-1 text-sm text-emerald-700">
                          <CheckCircle2 className="h-4 w-4" /> Em produção
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-sm text-amber-600">
                          <Clock className="h-4 w-4" /> Aguardando liberação na Separação
                        </span>
                      )}
                      {aberta ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>
                  {aberta && (o.carrinhos || []).length > 0 && (
                    <div className="border-t px-4 py-3">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                        Carrinhos desta ordem
                      </p>
                      <ul className="space-y-1.5 text-sm">
                        {o.carrinhos.map((c) => (
                          <li
                            key={c.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2"
                          >
                            <span className="font-mono font-semibold text-gray-900">{c.lote_carrinho || '—'}</span>
                            <span className="text-gray-700">
                              {c.setor_atual_nome || '—'}
                              {c.etapa_atual_nome && c.etapa_atual_nome !== c.setor_atual_nome
                                ? ` · ${c.etapa_atual_nome}`
                                : ''}
                            </span>
                            <span
                              className={`text-xs font-medium ${
                                c.status === 'em_processamento'
                                  ? 'text-orange-700'
                                  : c.status === 'disponivel'
                                    ? 'text-emerald-700'
                                    : 'text-gray-500'
                              }`}
                            >
                              {rotuloStatusCarrinho(c.status)}
                              {c.quantidade ? ` · ${c.quantidade}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default FluxoSetorialPage;
